import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { Icons } from 'utoniq-ui'
import { SkeletonText } from 'utoniq-ui'

import { MonthlyEventsByArtistFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { config, constants } from '@/config'
import { useRouter } from '@/core'
import { Organism } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'
import { dayjs } from '@/lib/dayjs'

export type EventScheduledCardProps = {
  scheduledEvent?: {
    date: string
    events?: MonthlyEventsByArtistFragment[]
  }
  month: string
  className: string
}

export const EventScheduledCard: Organism<EventScheduledCardProps> = ({
  scheduledEvent,
  month,
  className,
}) => {
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()

  return (
    <Stack className={className} direction="row">
      <Box minWidth="57px">
        <Typography variant="h6">
          {dayjs(scheduledEvent?.date).format('D(ddd)')}
        </Typography>
      </Box>
      <Stack spacing={1} p="1" width="100%">
        {scheduledEvent?.events?.map(event => (
          <Card key={event.id} sx={{ borderRadius: 1 }}>
            <CardActionArea
              href={generatePath(routerPages.ShokufesEventDetail, {
                eventId: event.id,
              })}
              sx={{ height: '100%', p: 1 }}
              onClick={() => {
                analytics?.track('click_select', {
                  applicationKey: config.applicationKey,
                  eventId: event.id,
                  name: 'eventFes',
                })
                sessionStorage.setItem(constants.storageKeys.EVENT_MONTH, month)
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={2}
                sx={{ p: 1 }}
              >
                <CardMedia
                  sx={{ minWidth: '68px', height: '68px' }}
                  image={event.thumbnail?.mediumUri ?? ''}
                  title={event.title}
                />
                <CardContent sx={{ padding: '0!important' }}>
                  <Typography
                    variant="h6"
                    sx={{ pb: 1, ...styleSets.lineCamp(2) }}
                  >
                    {event.title}
                  </Typography>
                  {event.dateRange && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icons.Calendar
                        fontSize="small"
                        sx={{ color: 'grey.700' }}
                      />
                      <Typography variant="overline" color="text.secondary">
                        {event.dateRange
                          .replace(/（/g, '(')
                          .replace(/）/g, ')')}
                      </Typography>
                    </Stack>
                  )}
                  {event.location && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ pt: 1 }}
                    >
                      <Icons.Pin fontSize="small" sx={{ color: 'grey.700' }} />
                      <Typography variant="overline" color="text.secondary">
                        {event.location?.approximateAddress}
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Stack>
  )
}

export const EventScheduledCardSkeleton: FC = () => (
  <Stack direction="row" py="10px" width="100%" spacing={5}>
    <Skeleton
      variant="rectangular"
      sx={{ minWidth: '85px', minHeight: '68px' }}
    />
    <Stack
      width="100%"
      minHeight="68px"
      maxHeight="68px"
      justifyContent="space-between"
    >
      <SkeletonText variant="h4" width="100%" />
      <SkeletonText variant="h4" width="100%" />
      <SkeletonText variant="h4" width="100%" />
    </Stack>
  </Stack>
)
